import { useState, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import LikeOutline from '@simplywallst/ui-core/icons/LikeOutline';
import LikeSolid from '@simplywallst/ui-core/icons/LikeSolid';

import If from '@/components/If';
import useUser from '@/hooks/useUser';

import { useVoteOnNarrative } from '../../hooks/useVoteOnNarrative';
import { useUserVotedNarratives } from '../../hooks/useUserVotedNarratives';
import { Button } from '@simplywallst/ui-core/next';

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal')
);

export interface Props {
  narrativeId: string;
  slug: string;
  upVotes: number;
}

const NarrativeVoting = ({ narrativeId, slug, upVotes }: Props) => {
  const [showRegoModal, setShowRegoModal] = useState(false);
  const {
    data: { loggedIn },
  } = useUser();

  const { data: UserVotedNarrativesResponse, isLoading } =
    useUserVotedNarratives({}, { enabled: loggedIn });
  const UserVotedNarratives = UserVotedNarrativesResponse?.UserVotedNarratives;
  const isUpVoted = useMemo(
    () => !!UserVotedNarratives?.find((n) => n.narrativeId === narrativeId),
    [UserVotedNarratives, narrativeId]
  );
  const { mutateAsync: toggleVote } = useVoteOnNarrative(slug, isUpVoted);
  const { trackEvent } = useTracking();

  return (
    <>
      <If match={showRegoModal}>
        {() => (
          <GuestRegistrationModal
            canClose={true}
            removeWindowScroll={true}
            onLoginSuccess={() => {
              setShowRegoModal(false);
            }}
            onRegisterSuccess={() => {
              setShowRegoModal(false);
            }}
            onClose={() => {
              setShowRegoModal(false);
              trackEvent({
                type: 'track',
                subject: 'guest registration modal',
                modifier: 'dismissed',
                action: 'click',
              });
            }}
          />
        )}
      </If>

      <Button
        data-cy-id="narrative-upvote"
        variant="outline"
        isLoading={isLoading}
        isDisabled={isLoading}
        size="medium"
        className="!px-x1"
        onClick={() => {
          if (!loggedIn) {
            setShowRegoModal(true);
            return;
          }

          toggleVote({ narrativeId });
          if (isUpVoted) {
            trackEvent({ action: 'clicked', modifier: 'downVote' });
          } else {
            trackEvent({ action: 'clicked', modifier: 'upVote' });
          }
        }}
      >
        {isUpVoted ? <LikeSolid /> : <LikeOutline />}
        {upVotes}
        <span className="sr-only"> votes</span>
      </Button>
    </>
  );
};

export default NarrativeVoting;
